import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { ApiError } from '../lib/api'
import styles from './Auth.module.css'

export default function LoginPage() {
  const { login, totpPending, verifyTotp, loading, claims } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      // If no TOTP, AuthContext sets claims and we redirect below
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleTotp(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await verifyTotp(totpCode)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Invalid code')
      setTotpCode('')
    } finally {
      setSubmitting(false)
    }
  }

  // Redirect only once claims are actually set (not just when loading stops)
  React.useEffect(() => {
    if (!loading && !totpPending && claims) {
      navigate('/app', { replace: true })
    }
  }, [loading, totpPending, claims, navigate])

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <span className={styles.logoMark}>CH</span>
          <span className={styles.logoText}>Cyberhelix</span>
        </div>
        <h1 className={styles.title}>
          {totpPending ? 'Two-Factor Auth' : 'Control Panel'}
        </h1>
        <p className={styles.subtitle}>
          {totpPending
            ? 'Enter the 6-digit code from your authenticator app'
            : 'Sign in to your account'}
        </p>

        {error && <div className={styles.error} role="alert">{error}</div>}

        {!totpPending ? (
          <form onSubmit={handleLogin} className={styles.form}>
            <label className={styles.label}>
              Email
              <input
                type="email"
                className={styles.input}
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                required
                autoFocus
              />
            </label>
            <label className={styles.label}>
              Password
              <input
                type="password"
                className={styles.input}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button
              type="submit"
              className={styles.btn}
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleTotp} className={styles.form}>
            <label className={styles.label}>
              Authenticator code
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                className={`${styles.input} ${styles.inputCenter}`}
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoComplete="one-time-code"
                required
                autoFocus
              />
            </label>
            <button
              type="submit"
              className={styles.btn}
              disabled={submitting || totpCode.length !== 6}
              aria-busy={submitting}
            >
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
